import React, { useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { getSmartImage } from '../../helpers/helpers';
import './CatalogVariantGrid.scss';

export const useDeferredResizeMeasure = (ref, measure) => {
  const measureRef = useRef(measure);
  measureRef.current = measure;
  useLayoutEffect(() => {
    const node = ref.current;
    measureRef.current(node);
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    let frame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => measureRef.current(node));
    });
    observer.observe(node);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [ref]);
};

// The panel slot spans the full grid width, so DOM position decides which row
// it lands under; splice it after the last card of the expanded card's row.
export const CatalogVariantGrid = ({ cardsPerRow, expandedKey, items, keyOf, renderCard, renderPanel }) => {
  const expandedIndex = expandedKey == null ? -1 : items.findIndex(item => keyOf(item) === expandedKey);
  const panelAfter = expandedIndex === -1 ? -1 : cardsPerRow ? Math.min(items.length, (Math.floor(expandedIndex / cardsPerRow) + 1) * cardsPerRow) : items.length;
  return (
    <div
      className='catalogs-variant-grid'
      style={cardsPerRow ? { gridTemplateColumns: `repeat(${cardsPerRow}, minmax(0, 1fr))` } : undefined}
    >
      {(panelAfter === -1 ? items : items.slice(0, panelAfter)).map(renderCard)}
      {panelAfter !== -1 && <div className='catalogs-variant-panel-slot'>{renderPanel()}</div>}
      {panelAfter !== -1 && items.slice(panelAfter).map(renderCard)}
    </div>
  );
};

export const CatalogVariantCard = ({ badges = [], isChanged, isExpanded, note, onToggle, previousCounts, variant }) => (
  <button aria-expanded={isExpanded} className={cn('catalogs-variant-card', { changed: isChanged, expanded: isExpanded })} onClick={onToggle}>
    <div className='catalogs-variant-card-image'>
      {variant.imageUrl ? <img alt='' src={getSmartImage(variant.imageUrl)} /> : <span>{String(variant.name || '').slice(0, 1)}</span>}
      {badges.length > 0 && (
        <div className='catalogs-variant-card-badges'>
          {badges.map(badge => (
            <span className={cn('catalogs-status-badge', badge.tone)} key={badge.label}>
              {badge.label}
            </span>
          ))}
        </div>
      )}
    </div>
    <div className='catalogs-variant-card-body'>
      <strong>{variant.name}</strong>
      <span>{variant.productType || 'Uncategorized'}</span>
      <div className='catalogs-variant-card-meta'>
        <CountWithDelta label={variant.sourceProductCount === 1 ? 'source' : 'sources'} previous={previousCounts?.sourceProductCount} value={variant.sourceProductCount} />
        <span>·</span>
        <CountWithDelta
          label={displaySiblingCount(variant) === 1 ? 'sibling in stock' : 'siblings in stock'}
          previous={previousCounts ? displaySiblingCount(previousCounts) : null}
          value={displaySiblingCount(variant)}
        />
      </div>
      {note && <small>{note}</small>}
    </div>
  </button>
);

export const catalogVisibilityBadge = variant => (variant?.hideFromPublicCatalog ? { label: 'Hidden' } : null);

export const CatalogGhostCard = ({ label, note }) => (
  <div className='catalogs-variant-card ghost'>
    <strong>{label}</strong>
    {note && <span>{note}</span>}
  </div>
);

export const CatalogVariantPanel = ({
  badges = [],
  error,
  imageUrl,
  isLoading,
  name,
  notes = [],
  onClose,
  previousName,
  productType,
  siblings = [],
  sources = []
}) => (
  <div className='catalogs-variant-panel'>
    <div className='catalogs-variant-panel-header'>
      <div className='catalogs-variant-panel-thumb'>
        {imageUrl ? <img alt='' src={getSmartImage(imageUrl)} /> : <span>{String(name || '').slice(0, 1)}</span>}
      </div>
      <div className='catalogs-variant-panel-title'>
        <h3>
          {previousName && previousName !== name ? (
            <>
              <s>{previousName}</s>
              <span aria-hidden='true'>→</span>
              {name}
            </>
          ) : (
            name
          )}
        </h3>
        <div className='catalogs-variant-panel-sub'>
          {productType && <span>{productType}</span>}
          {badges.map(badge => (
            <span className={cn('catalogs-status-badge', badge.tone)} key={badge.label}>
              {badge.label}
            </span>
          ))}
        </div>
        {notes.length > 0 && (
          <ul className='catalogs-variant-panel-notes'>
            {notes.map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </div>
      <button aria-label='Close details' className='catalogs-variant-panel-close' onClick={onClose}>
        ✕
      </button>
    </div>
    {error ? (
      <div className='catalogs-error'>{error}</div>
    ) : isLoading ? (
      <div className='catalogs-empty compact'>Loading product details…</div>
    ) : (
      <>
        <section className='catalogs-variant-panel-section'>
          <div className='catalogs-variant-panel-section-heading'>
            <strong>Sources</strong>
            <span>{sources.length.toLocaleString()}</span>
          </div>
          <CatalogSourceList sources={sources} />
        </section>
        <section className='catalogs-variant-panel-section'>
          <div className='catalogs-variant-panel-section-heading'>
            <strong>Variant siblings</strong>
            <span>{siblings.length.toLocaleString()}</span>
          </div>
          <CatalogSiblingTable productTitle={name} siblings={siblings} sources={sources} />
        </section>
      </>
    )}
  </div>
);

const CatalogSourceList = ({ limit = 10, sources = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const visibleSources = showAll ? sources : sources.slice(0, limit);
  const hiddenCount = Math.max(0, sources.length - limit);
  return (
    <div className='catalogs-product-source-list'>
      {visibleSources.map(source => {
        const annotation = sourceDispositionLabel(source);
        const content = (
          <>
            <strong>{source.title}</strong>
            <span>{[source.color, source.handle, `${Number(source.siblingCount || 0).toLocaleString()} variant siblings`].filter(Boolean).join(' · ')}</span>
            {annotation && <em>{annotation}</em>}
          </>
        );
        if (!source.sourceUrl) {
          return <div className={cn('catalogs-product-source-card unpublished', source.disposition)} key={source.sourceProductId}>{content}</div>;
        }
        return (
          <a className={cn('catalogs-product-source-card', source.disposition)} href={source.sourceUrl} target='_blank' rel='noopener noreferrer' key={source.sourceProductId}>
            {content}
          </a>
        );
      })}
      {hiddenCount > 0 && (
        <button className='catalogs-product-source-more' onClick={() => setShowAll(value => !value)}>
          {showAll ? 'Show fewer sources' : `Show ${hiddenCount.toLocaleString()} more ${hiddenCount === 1 ? 'source' : 'sources'}`}
        </button>
      )}
    </div>
  );
};

export const CatalogSiblingTable = ({ productTitle, siblings = [], sources = [] }) => {
  const showPrice = siblings.some(sibling => sibling.price !== null && sibling.price !== undefined);
  const showStock = siblings.some(sibling => sibling.isAvailable !== null && sibling.isAvailable !== undefined);
  const showUrl = siblings.some(sibling => sibling.current && isChangedText(sibling.current.url, sibling.sourceUrl));
  const titleBySourceProductId = new Map();
  for (const source of sources) {
    const id = shopifyId(source.sourceProductId || source.shopifyProductId);
    if (id && source.title && !titleBySourceProductId.has(id)) titleBySourceProductId.set(id, source.title);
  }
  return (
    <div className='catalogs-sibling-table-wrap'>
      <table className='catalogs-sibling-table'>
        <thead>
          <tr>
            <th>Name</th>
            <th>Attributes</th>
            <th>sizeTag</th>
            <th>nonSizeTag</th>
            <th>Shopify variant ID</th>
            <th>SKU</th>
            {showUrl && <th>URL</th>}
            {showPrice && <th>Price</th>}
            {showStock && <th>Stock</th>}
          </tr>
        </thead>
        <tbody>
          {siblings.map((sibling, index) => {
            const attributes = sibling.attributes || [];
            const shopifyVariantId = shopifyId(sibling.shopifyVariantId || sibling.id);
            const movement = siblingMovementBadge(sibling);
            return (
              <tr className={cn({ departed: sibling.departure })} key={sibling.departure ? `departed-${sibling.id}` : shopifyVariantId || index}>
                <td className='catalogs-sibling-name'>
                  {siblingDisplayName(sibling, titleBySourceProductId.get(shopifyId(sibling.shopifyProductId)) || productTitle, attributes)}
                  {sibling.isNew && <span className='catalogs-status-badge added'>New</span>}
                  {movement && <span className={cn('catalogs-status-badge', movement.tone)}>{movement.label}</span>}
                </td>
                <td>
                  <div className='catalogs-sibling-attributes'>
                    {attributes.length
                      ? attributes.map((attribute, attributeIndex) => (
                          <div key={`${attribute.name}-${attributeIndex}`}>
                            <span>{attribute.name}</span>
                            <strong>{String(attribute.value)}</strong>
                          </div>
                        ))
                      : <span>—</span>}
                  </div>
                </td>
                <td className='catalogs-derived-tag'><DerivedTagValue current={sibling.current?.sizeTag} hasCurrent={Boolean(sibling.current)} proposed={sibling.sizeTag} /></td>
                <td className='catalogs-derived-tag'><DerivedTagValue current={sibling.current?.nonSizeTag} hasCurrent={Boolean(sibling.current)} proposed={sibling.nonSizeTag} /></td>
                <td className='mono'>
                  <DerivedTagValue current={displayId(sibling.current?.shopifyVariantId)} hasCurrent={Boolean(sibling.current)} proposed={shopifyVariantId} />
                  {sibling.current && isChangedText(displayId(sibling.current.shopifyProductId), displayId(sibling.shopifyProductId)) && (
                    <div className='catalogs-sibling-product-id'>
                      Product <DerivedTagValue current={displayId(sibling.current.shopifyProductId)} hasCurrent proposed={displayId(sibling.shopifyProductId)} />
                    </div>
                  )}
                </td>
                <td className='mono'>
                  <DerivedTagValue current={sibling.current?.sku} hasCurrent={Boolean(sibling.current)} proposed={sibling.sku} />
                </td>
                {showUrl && (
                  <td className='catalogs-sibling-url'>
                    <DerivedTagValue
                      current={urlLabel(sibling.current?.url, sibling.sourceUrl)}
                      hasCurrent={Boolean(sibling.current)}
                      proposed={urlLabel(sibling.sourceUrl, sibling.current?.url)}
                    />
                  </td>
                )}
                {showPrice && (
                  <td>
                    <DerivedTagValue current={displayPrice(sibling.current?.price)} hasCurrent={Boolean(sibling.current)} proposed={displayPrice(sibling.price)} />
                  </td>
                )}
                {showStock && (
                  <td className='catalogs-sibling-stock'>
                    <DerivedTagValue current={displayStock(sibling.current?.isAvailable)} hasCurrent={Boolean(sibling.current)} proposed={displayStock(sibling.isAvailable)} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const displayId = value => (value === null || value === undefined ? null : String(value));

const isChangedText = (left, right) => String(left ?? '').trim() !== String(right ?? '').trim();

// The host shows only when it differs, so a storefront-host swap stays visible without every row repeating it.
const urlLabel = (url, comparedUrl) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const comparedHost = comparedUrl ? new URL(comparedUrl).host : parsed.host;
    return `${parsed.host === comparedHost ? '' : parsed.host}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

const siblingMovementBadge = sibling => {
  if (sibling.departure === 'removed') return { label: 'No longer in the snapshot', tone: 'removed' };
  if (sibling.departure === 'removed_by_script') return { label: 'Removed by script', tone: 'removed' };
  if (sibling.departure === 'excluded') return { label: 'Excluded', tone: 'removed' };
  if (sibling.departure === 'moved') return { label: sibling.movedTo ? `Moved to ${sibling.movedTo}` : 'Moved out', tone: 'changed' };
  if (sibling.isMovedIn) return { label: sibling.movedFrom ? `From ${sibling.movedFrom}` : 'Moved in', tone: 'changed' };
  return null;
};

const displayPrice = value => (value === null || value === undefined ? null : Number(value).toFixed(2));

const displayStock = value => (value === null || value === undefined ? null : value ? 'In stock' : 'Out of stock');

const DerivedTagValue = ({ current, hasCurrent, proposed }) => {
  const currentValue = current || null;
  const proposedValue = proposed || null;
  if (!hasCurrent || currentValue === proposedValue) return proposedValue || '—';
  return (
    <span className='catalogs-derived-tag-diff'>
      <s>{currentValue || '—'}</s>
      <span>→</span>
      <span className='next'>{proposedValue || '—'}</span>
    </span>
  );
};

const siblingDisplayName = (sibling, productTitle, attributes) => {
  const explicitName = String(sibling.name || '').trim();
  if (explicitName && !isAttributeOnlyName(explicitName, attributes)) return explicitName;
  const baseTitle = stripAttributeSuffix(String(productTitle || '').trim(), sibling, attributes);
  return baseTitle || explicitName || 'Default';
};

// Per-colorway listings often repeat the colorway in the title ('Clubhouse
// Range Jogger -- Vapor Gray'); once it's extracted into the sibling's
// attributes the suffix is redundant, so drop it from the display name.
const stripAttributeSuffix = (title, sibling, attributes) => {
  const match = title.match(/^(.+)\s+(?:--|–|—)\s+(.+)$/);
  if (!match) return title;
  const values = attributes.map(attribute => String(attribute.value).trim().toLowerCase());
  for (const tag of [sibling.nonSizeTag, sibling.sizeTag]) {
    if (tag) values.push(String(tag).trim().toLowerCase());
  }
  return values.includes(match[2].trim().toLowerCase()) ? match[1].trim() : title;
};

const isAttributeOnlyName = (name, attributes) => {
  if (name.toLowerCase() === 'default title') return true;
  const values = attributes.map(attribute => String(attribute.value).trim().toLowerCase());
  // Shopify joins option values with ' / ' (spaced); splitting on every slash
  // would break values like 'O/S' or 'Natural/Cacao' into non-matching parts.
  const parts = name.split(/\s+\/\s+/).map(value => value.trim().toLowerCase()).filter(Boolean);
  return parts.length > 0 && parts.every(part => values.includes(part));
};

const shopifyId = value => String(value || '').split('/').pop();

const sourceDispositionLabel = source => {
  const counterpart = source.counterpartIsHidden ? `${source.counterpartVariantName} (hidden)` : source.counterpartVariantName;
  if (source.disposition === 'moved_in') return source.counterpartVariantName ? `Moved in from ${counterpart}` : 'Moved in';
  if (source.disposition === 'moved_out') return source.counterpartVariantName ? `Moved to ${counterpart}` : 'Moved out';
  if (source.disposition === 'new') return 'New listing';
  if (source.disposition === 'ignored') return source.reason || 'Excluded by script';
  if (source.disposition === 'retired') return 'No longer in the snapshot';
  return null;
};

const displaySiblingCount = variant => Number(variant.availableSiblingCount ?? variant.siblingCount ?? 0);

const CountWithDelta = ({ label, previous, value }) => {
  const current = Number(value || 0);
  const before = previous == null ? current : Number(previous || 0);
  if (before === current) {
    return (
      <span>
        {current.toLocaleString()} {label}
      </span>
    );
  }
  return (
    <span className='catalogs-variant-card-delta'>
      <s>{before.toLocaleString()}</s> → {current.toLocaleString()} {label}
    </span>
  );
};
